import React, { useState, useEffect } from 'react';
import logo from '../V Clinic logo - Blue gradient.PNG';

const FrontDeskDashboard = ({ onSelectPatient, onLogout }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

    // Role identification variables
    const staffName = localStorage.getItem('doctor_name'); // Kept as doctor_name for API compatibility
    const staffId = localStorage.getItem('doctor_id');
    const userBranch = localStorage.getItem('user_branch');

    // 🛰️ 1. FETCH TODAY'S QUEUE (Strictly Filtered by Branch)
    useEffect(() => {
        const fetchQueue = async () => {
            setLoading(true);
            try {
                // 🛡️ SECURITY: We fetch ONLY the branch schedule. No global search allowed.
                const response = await fetch(
                    `/api/appointments?branch=${encodeURIComponent(userBranch)}&doctorId=${staffId}&date=${selectedDate}&role=frontdesk`
                );
                if (!response.ok) throw new Error('Odoo fetch failed');
                const data = await response.json();
                setAppointments(data); 
            } catch (err) {
                console.error("Front Desk Dashboard error:", err);
            } finally {
                setLoading(false);
            }
        };
        if (staffId && userBranch && selectedDate) fetchQueue();
    }, [staffId, userBranch, selectedDate]); 

    // 📝 2. TELEPORT BRIDGE
    const handleOpenPatient = (p) => {
        // Send Log to Odoo so you still have a record of who opened it
        fetch('/api/logs/access', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                hn: p.patient_hn,
                doctorId: staffId,
                branchId: localStorage.getItem('user_branch_id'),
                type: "Front Desk Queue Access",
                reason: "Normal Visit Today" 
            })
        }).catch(err => console.error("Log failed:", err));

        // Teleport to Canvas Page
        onSelectPatient({
            ...p,
            name: p.patient_name, 
            doctor: p.doctor || '',
            branch: userBranch
        });
    };

    // 🕒 3. FILTER LIST (Quick local search for today's queue only)
    const filteredAppointments = appointments
        .filter(appt => {
            const name = (appt.patient_name || "").toString().toLowerCase();
            const hn = (appt.patient_hn || "").toString().toLowerCase();
            return name.includes(searchTerm.toLowerCase()) || hn.includes(searchTerm.toLowerCase());
        })
        .sort((a, b) => (a.time || "00:00").localeCompare(b.time || "00:00"));

    // --- CARD RENDERER ---
    const renderPatientCard = (p) => {
        let colors = { bg: '#F5F5F5', text: '#757575', border: '#E0E0E0' };
        const s = p.status;
        if (s === 'ลูกค้ามาถึง') colors = { bg: '#FFF9C4', text: '#FBC02D', border: '#FFF176' };
        else if (s === 'พบแพทย์') colors = { bg: '#E3F2FD', text: '#1976D2', border: '#90CAF9' };
        else if (s === 'ตรวจเสร็จ' || s === 'ชำระเงินแล้ว') colors = { bg: '#E8F5E9', text: '#2E7D32', border: '#A5D6A7' };
        else if (s === 'ไม่มาตามนัด') colors = { bg: '#FFEBEE', text: '#C62828', border: '#EF9A9A' };

        return (
            <div key={p.id || p.patient_hn} style={styles.patientCard}>
                <div style={styles.cardHeader}>
                    <span style={styles.timeTag}>{`🕒 ${p.time}`}</span>
                </div>
                <h3 style={styles.patientName}>{p.patient_name}</h3>
                <p style={styles.hnText}>HN: {p.patient_hn}</p>
                <div style={{ marginBottom: '15px' }}>
                    <span style={{ ...styles.statusTag, background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
                        {p.status || 'รอ'}
                    </span>
                </div>
                <button 
                    onClick={() => handleOpenPatient(p)} 
                    style={{ ...styles.openBtn, background: '#4CAF50' }}
                >
                    Open Patient OPD
                </button>
            </div>
        );
    };

    return (
        <div style={styles.container}>
            {/* --- HEADER --- */}
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <img src={logo} alt="Logo" style={{ width: '100px' }} />
                    <div>
                        <h1 style={{margin: 0}}>{staffName} <span style={{fontSize: '14px', color: '#888', fontWeight: 'normal'}}>(Front Desk)</span></h1>
                        <p style={{color: '#666'}}>สาขา {userBranch} | {new Date(selectedDate).toLocaleDateString('th-TH')}</p>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                    <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={styles.datePicker} />
                    <button onClick={onLogout} style={styles.logoutBtn}>Logout</button>
                </div>
            </div>

            {/* --- LOCAL FILTER ONLY --- */}
            <div style={styles.searchSection}>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <input 
                        placeholder="Filter today's active queue..." 
                        style={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {/* Global search button completely removed */}
                </div>
            </div>

            {/* --- TODAY LIST --- */}
            <h2 style={styles.sectionTitle}>Today's Schedule</h2>
            <div style={styles.queueGrid}>
                {loading ? <p>Loading Schedule...</p> : filteredAppointments.map(p => renderPatientCard(p))}
                {!loading && filteredAppointments.length === 0 && <p style={{color: '#999'}}>No appointments found for this date.</p>}
            </div>
        </div>
    );
};

const styles = {
    container: { padding: '40px', background: '#f4f7f9', minHeight: '100vh' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' },
    datePicker: { padding: '10px', borderRadius: '8px', border: '1px solid #ddd' },
    logoutBtn: { padding: '10px 20px', borderRadius: '8px', background: '#fff', border: '2px solid #ef5350', color: '#c62828', fontWeight: 'bold', cursor: 'pointer' },
    searchSection: { marginBottom: '30px' },
    searchInput: { flex: 1, padding: '20px', borderRadius: '15px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', fontSize: '18px' },
    sectionTitle: { fontSize: '22px', color: '#333', marginBottom: '20px' },
    queueGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' },
    patientCard: { background: '#fff', padding: '25px', borderRadius: '20px', boxShadow: '0 10px 20px rgba(0,0,0,0.05)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '15px' },
    timeTag: { background: '#e3f2fd', color: '#1976d2', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' },
    statusTag: { padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' },
    patientName: { margin: '0 0 5px 0', fontSize: '20px', color: '#333' },
    hnText: { color: '#888', margin: '0 0 5px 0', fontSize: '14px' },
    openBtn: { width: '100%', padding: '12px', borderRadius: '10px', border: 'none', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }
};

export default FrontDeskDashboard;